import { useCallback, useEffect, useRef, useState } from 'react';

interface UseSliderProps {
	slidesCount: number;
	autoPlayInterval?: number;
}

export const useSlider = ({ slidesCount, autoPlayInterval }: UseSliderProps) => {
	const slidesContainerRef = useRef<HTMLDivElement>(null);
	const [currentSlide, setCurrentSlide] = useState(0);
	const isProgrammaticScroll = useRef(false);
	const timeoutRef = useRef<NodeJS.Timeout | null>(null);

	const handleSlideChange = useCallback((index: number) => {
		const container = slidesContainerRef.current;
		if (!container) return;
		isProgrammaticScroll.current = true;
		const slideWidth = container.clientWidth;
		container.scrollTo({
			left: slideWidth * index,
			behavior: 'smooth'
		});
		setCurrentSlide(index);
		if (timeoutRef.current) clearTimeout(timeoutRef.current);
		timeoutRef.current = setTimeout(() => {
			isProgrammaticScroll.current = false;
		}, 500);
	}, []);

	const handleScroll = useCallback(() => {
		if (isProgrammaticScroll.current) return;
		const container = slidesContainerRef.current;
		if (!container) return;
		const slideWidth = container.clientWidth;
		if (slideWidth === 0) return;
		const newCurrentSlide = Math.round(container.scrollLeft / slideWidth);
		if (newCurrentSlide !== currentSlide) {
			setCurrentSlide(newCurrentSlide);
		}
	}, [currentSlide]);
	useEffect(() => {
		const container = slidesContainerRef.current;
		if (!container) return;
		container.addEventListener('scroll', handleScroll, { passive: true });
		return () => {
			container.removeEventListener('scroll', handleScroll);
			if (timeoutRef.current) clearTimeout(timeoutRef.current);
		};
	}, [handleScroll]);

	useEffect(() => {
		if (!autoPlayInterval || slidesCount <= 0) return;

		const timer = setInterval(() => {
			const nextSlide = (currentSlide + 1) % slidesCount;
			handleSlideChange(nextSlide);
		}, autoPlayInterval);
		return () => clearInterval(timer);
	}, [autoPlayInterval, slidesCount, currentSlide, handleSlideChange]);
	useEffect(() => {
		if (slidesContainerRef.current) {
			slidesContainerRef.current.scrollLeft = 0;
		}
		setCurrentSlide(0);
		isProgrammaticScroll.current = false;
	}, [slidesCount]);

	return {
		currentSlide,
		slidesContainerRef,
		handleSlideChange
	};
};
